import { auth } from '../config/firebase';
import { connectAuthEmulator, signInAnonymously, signOut, createUserWithEmailAndPassword } from 'firebase/auth';
import { diagnosticsConfig } from '../config/diagnostics';

export interface DiagnosticResult {
  test: string;
  status: 'success' | 'error' | 'running';
  message: string;
  timestamp: string;
  duration?: number;
}

class DiagnosticsService {
  private testResults: DiagnosticResult[] = [];

  async runAuthEmulatorConnectivityTest(): Promise<DiagnosticResult[]> {
    this.testResults = [];
    
    // Test 1: Check if emulator endpoint is reachable
    await this.testEmulatorEndpoint();
    
    // Test 2: Test actual signup flow (not just anonymous)
    await this.testSignupFlow();
    
    // Test 3: Test auth state persistence
    await this.testAuthStatePersistence();
    
    return this.testResults;
  }

  private async testEmulatorEndpoint(): Promise<void> {
    const startTime = Date.now();
    this.addResult({
      test: 'Emulator Endpoint Connectivity',
      status: 'running',
      message: `Testing connection to ${diagnosticsConfig.authEmulatorEndpoint}...`,
      timestamp: new Date().toISOString()
    });

    try {
      const response = await fetch(`${diagnosticsConfig.authEmulatorEndpoint}/emulator/v1/projects/demo-project/config`);
      const duration = Date.now() - startTime;
      
      if (response.ok) {
        this.updateLastResult({
          status: 'success',
          message: `✅ Successfully connected to Firebase Auth Emulator (${duration}ms)`,
          duration
        });
      } else {
        this.updateLastResult({
          status: 'error',
          message: `❌ Emulator responded with status: ${response.status}`,
          duration
        });
      }
    } catch (error: any) {
      const duration = Date.now() - startTime;
      this.updateLastResult({
        status: 'error',
        message: `❌ Failed to connect to emulator: ${error.message}`,
        duration
      });
    }
  }

  private async testSignupFlow(): Promise<void> {
    const startTime = Date.now();
    this.addResult({
      test: 'Signup Flow Test',
      status: 'running',
      message: 'Testing actual signup flow with emulator...',
      timestamp: new Date().toISOString()
    });

    try {
      // Create a test user with unique email
      const testEmail = `test-${Date.now()}@example.com`;
      const testPassword = 'test123456';
      
      const userCredential = await createUserWithEmailAndPassword(auth, testEmail, testPassword);
      const duration = Date.now() - startTime;
      
      this.updateLastResult({
        status: 'success',
        message: `✅ Signup flow successful. UID: ${userCredential.user.uid} (${duration}ms)`,
        duration
      });

      // Clean up - delete the test user
      if (userCredential.user) {
        await userCredential.user.delete();
      }
    } catch (error: any) {
      const duration = Date.now() - startTime;
      this.updateLastResult({
        status: 'error',
        message: `❌ Signup flow failed: ${error.message}`,
        duration
      });
    }
  }

  private async testAuthStatePersistence(): Promise<void> {
    const startTime = Date.now();
    this.addResult({
      test: 'Auth State Persistence',
      status: 'running',
      message: 'Testing auth state listener...',
      timestamp: new Date().toISOString()
    });

    try {
      return new Promise((resolve) => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
          const duration = Date.now() - startTime;
          
          this.updateLastResult({
            status: 'success',
            message: `✅ Auth state listener working. Current user: ${user ? user.uid : 'none'} (${duration}ms)`,
            duration
          });
          
          unsubscribe();
          resolve();
        });
        
        // Timeout after 5 seconds
        setTimeout(() => {
          const duration = Date.now() - startTime;
          this.updateLastResult({
            status: 'error',
            message: `❌ Auth state listener timeout (${duration}ms)`,
            duration
          });
          unsubscribe();
          resolve();
        }, 5000);
      });
    } catch (error: any) {
      const duration = Date.now() - startTime;
      this.updateLastResult({
        status: 'error',
        message: `❌ Auth state test failed: ${error.message}`,
        duration
      });
    }
  }

  private addResult(result: DiagnosticResult): void {
    this.testResults.push(result);
  }

  private updateLastResult(updates: Partial<DiagnosticResult>): void {
    if (this.testResults.length > 0) {
      const lastResult = this.testResults[this.testResults.length - 1];
      Object.assign(lastResult, updates);
    }
  }

  getEmulatorInfo(): any {
    return {
      endpoint: diagnosticsConfig.authEmulatorEndpoint,
      connected: auth.app.options.projectId,
      currentUser: auth.currentUser?.uid || 'none',
      isEmulatorMode: diagnosticsConfig.authEmulatorEndpoint.includes('localhost') || 
                      diagnosticsConfig.authEmulatorEndpoint.includes('127.0.0.1')
    };
  }
}

export const diagnosticsService = new DiagnosticsService();
